import { Compass, X, Phone, Loader2, Globe, MessageCircle, ExternalLink, Sparkles, FileText, Paperclip } from "lucide-react";

export const AttachmentChips = ({ attachments, uploading, onRemove }) => {
  if (!attachments.length && !uploading) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-attachments">
      {attachments.map((a) => (
        <div key={a.id} className="relative flex items-center gap-2 rounded-lg bg-[#0B111E] border border-[#223552] pr-7 pl-1.5 py-1 text-xs text-slate-200" data-testid={`chat-attachment-${a.id}`}>
          {a.preview ? <img src={a.preview} alt="" className="h-8 w-8 rounded object-cover" /> : <span className="h-8 w-8 rounded bg-[#172338] flex items-center justify-center"><FileText className="h-4 w-4 text-amber-400" /></span>}
          <span className="max-w-[140px] truncate">{a.original_filename}{a.pages ? <span className="text-slate-500"> · {a.pages}p</span> : null}</span>
          <button type="button" onClick={() => onRemove(a.id)} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-red-400" aria-label="Remove attachment" data-testid={`chat-attachment-remove-${a.id}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {uploading && <span className="inline-flex items-center gap-1.5 text-xs text-amber-300" data-testid="chat-attachment-uploading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</span>}
    </div>
  );
};

const BubbleAttachments = ({ attachments }) => (
  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="bubble-attachments">
    {attachments.map((a) => a.preview
      ? <img key={a.id} src={a.preview} alt={a.original_filename} className="h-20 w-20 rounded-lg object-cover border border-[#0B111E]/30" />
      : <span key={a.id} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B111E]/15 px-2 py-1 text-xs"><Paperclip className="h-3 w-3" /> {a.original_filename}</span>)}
  </div>
);

export const US_STATES = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming","District of Columbia"];

export const PROMPTS = [
  "My ex is denying my scheduled weekend visitation — what should I do today?",
  "How is child support calculated if I request 50/50 custody?",
  "What evidence helps prove my daily involvement in family court?",
  "How do I respond calmly to high-conflict texts without hurting my case?",
];

export const ChatHeader = ({ onClose }) => (
  <>
    <div className="bg-[#111B2C] border-b border-[#223552] px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#E5A93C] to-[#C86A36] flex items-center justify-center">
          <Compass className="h-5 w-5 text-[#0B111E]" strokeWidth={2.2} />
        </div>
        <div className="leading-tight">
          <p className="font-heading font-bold text-white text-sm">Father's Compass</p>
          <p className="text-[11px] text-emerald-400 flex items-center gap-1"><span className="h-1.5 w-1.5 bg-emerald-400 rounded-full aff-pulse" /> Here for you</p>
        </div>
      </div>
      <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="chat-close"><X className="h-5 w-5" /></button>
    </div>
    <div className="bg-crimson/10 border-b border-crimson/30 px-4 py-2 flex items-center gap-2">
      <Phone className="h-3.5 w-3.5 text-crimson shrink-0" />
      <p className="text-[11px] text-slate-300">In crisis? Call or text <a href="tel:988" className="text-crimson font-semibold underline">988</a> anytime. Educational support only — not legal advice or therapy.</p>
    </div>
  </>
);

export const StateSelect = ({ value, onChange }) => (
  <div className="px-4 py-2 border-b border-[#223552] flex items-center gap-2">
    <span className="text-[11px] text-slate-400 shrink-0">Your state:</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="chat-state-select"
      className="flex-1 bg-[#172338] border border-[#223552] rounded-md text-xs text-slate-200 px-2 py-1 outline-none focus:border-amber-400">
      <option value="">Select (optional)</option>
      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  </div>
);

const tabClass = (active) => `flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium border transition-colors ${
  active ? "bg-[#172338] border-amber-400/50 text-white" : "border-[#223552] text-slate-400 hover:text-slate-200"}`;

export const ModeTabs = ({ mode, setMode, stateSel, researchEnabled }) => (
  <>
    <div className="px-4 py-2 border-b border-[#223552] grid grid-cols-2 gap-1.5 bg-[#0B111E]" data-testid="chat-mode-tabs">
      <button onClick={() => setMode("chat")} data-testid="chat-mode-chat" className={tabClass(mode === "chat")}>
        <MessageCircle className="h-3.5 w-3.5" /> Guide
      </button>
      <button onClick={() => setMode("research")} data-testid="chat-mode-research" className={tabClass(mode === "research")}
        title={researchEnabled ? "Live web research with citations" : "Live research not configured yet"}>
        <Globe className="h-3.5 w-3.5" /> Research {stateSel ? `· ${stateSel}` : ""}
        {!researchEnabled && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-400" />}
      </button>
    </div>
    {mode === "research" && (
      <div className="px-4 py-2 border-b border-[#223552] bg-[#111B2C] text-[11px] text-slate-300" data-testid="chat-research-hint">
        {researchEnabled
          ? "Ask a state-specific legal question. I'll search the live web—statutes, court self-help sites, official sources—and cite every source."
          : "Live research isn't switched on yet. Ask the site owner to add a Perplexity API key."}
      </div>
    )}
  </>
);

const SourceList = ({ sources }) => (
  <div className="mt-3 pt-2.5 border-t border-[#223552]" data-testid="chat-sources">
    <p className="text-[10px] uppercase tracking-wider text-amber-400 flex items-center gap-1 mb-1.5"><Globe className="h-3 w-3" /> Live sources</p>
    <ol className="space-y-1">
      {sources.map((s, i) => (
        <li key={s.url} className="text-xs">
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-amber-300 inline-flex items-start gap-1.5" data-testid={`chat-source-${i}`}>
            <span className="font-mono text-amber-400/80 shrink-0">[{s.id ?? i + 1}]</span>
            <span className="truncate max-w-[260px]">{s.title || s.url}</span>
            <ExternalLink className="h-3 w-3 shrink-0 mt-0.5 text-slate-500" />
          </a>
        </li>
      ))}
    </ol>
  </div>
);

const Bubble = ({ m, showSpinner }) => {
  const isUser = m.role === "user";
  const body = m.researching && !m.content
    ? <span className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin text-amber-400" /> Searching official sources…</span>
    : m.content || (showSpinner ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : "");
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser ? "bg-[#E5A93C] text-[#0B111E] font-medium rounded-br-sm" : "bg-[#172338] text-slate-200 rounded-bl-sm border border-[#223552]"}`}>
        {m.research && <p className="text-[10px] uppercase tracking-wider text-amber-400 flex items-center gap-1 mb-1.5"><Globe className="h-3 w-3" /> Live research</p>}
        {isUser && m.attachments?.length > 0 && <BubbleAttachments attachments={m.attachments} />}
        {body}
        {m.sources?.length > 0 && <SourceList sources={m.sources} />}
        {m.saved && <p className="mt-2 text-[10px] text-emerald-400" data-testid="chat-research-saved">Saved to your Research History for printing.</p>}
      </div>
    </div>
  );
};

export const MessageList = ({ messages, streaming, onPrompt, scrollRef }) => (
  <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
    {messages.map((m, i) => <Bubble key={m.id} m={m} showSpinner={streaming && i === messages.length - 1} />)}
    {messages.length === 1 && (
      <div className="pt-2 space-y-2">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Try asking</p>
        {PROMPTS.map((p, i) => (
          <button key={p} onClick={() => onPrompt(p)} data-testid={`chat-prompt-${i}`}
            className="block w-full text-left text-xs text-slate-300 bg-[#111B2C] hover:bg-[#172338] border border-[#223552] hover:border-amber-400/50 rounded-lg px-3 py-2 transition-colors">
            {p}
          </button>
        ))}
      </div>
    )}
  </div>
);
